import React, { useEffect, useRef, useState, MutableRefObject } from 'react'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'

interface IProps {
  aspectRatio?: 'taller' | 'wider'
  handle?: React.ReactNode
  handleSize?: number
  hover?: boolean
  leftImage: string
  leftImageAlt?: string
  leftImageCss?: object
  leftImageLabel?: string
  onSliderPositionChange?: (position: number) => void
  rightImage: string
  rightImageAlt?: string
  rightImageCss?: object
  rightImageLabel?: string
  skeleton?: React.ReactNode
  sliderLineColor?: string
  sliderLineWidth?: number
  sliderPositionPercentage?: number
}

const CompareImage: React.FC<IProps> = (props: IProps) => {
  const {
    aspectRatio = 'taller',
    handleSize = 32,
    hover = false,
    leftImage,
    leftImageAlt = '',
    // leftImageCss = {},
    leftImageLabel = null,
    onSliderPositionChange = () => {},
    rightImage,
    rightImageAlt = '',
    // rightImageCss = {},
    rightImageLabel = null,
    skeleton = null,
    sliderLineColor = '#ffffff',
    sliderLineWidth = 3,
    sliderPositionPercentage = 0.5
  } = props

  // 0 to 1
  const [sliderPosition, setSliderPosition] = useState<number>(sliderPositionPercentage)
  const [containerWidth, setContainerWidth] = useState<number>(0)
  const [containerHeight, setContainerHeight] = useState<number>(0)
  const [leftImgLoaded, setLeftImgLoaded] = useState<boolean>(false)
  const [rightImgLoaded, setRightImgLoaded] = useState<boolean>(false)

  const containerRef = useRef() as MutableRefObject<HTMLDivElement>
  const rightImageRef = useRef() as MutableRefObject<HTMLImageElement>
  const leftImageRef = useRef() as MutableRefObject<HTMLImageElement>

  // make the component responsive
  useEffect(() => {
    const containerElement = containerRef.current
    const resizeObserver = new ResizeObserver(([entry]) => {
      const currentContainerWidth = entry.target.getBoundingClientRect().width
      setContainerWidth(currentContainerWidth)
    })
    resizeObserver.observe(containerElement)

    return () => resizeObserver.disconnect()
  }, [])

  useEffect(() => {
    // consider the case where loading image is completed immediately
    // due to the cache etc.
    const alreadyDone = leftImageRef.current.complete
    alreadyDone && setLeftImgLoaded(true)

    return () => {
      // when the left image source is changed
      setLeftImgLoaded(false)
    }
  }, [leftImage])

  useEffect(() => {
    // consider the case where loading image is completed immediately
    // due to the cache etc.
    const alreadyDone = rightImageRef.current.complete
    alreadyDone && setRightImgLoaded(true)

    return () => {
      // when the right image source is changed
      setRightImgLoaded(false)
    }
  }, [rightImage])

  const allImagesLoaded = rightImgLoaded && leftImgLoaded

  useEffect(() => {
    const handleSliding = (event: any) => {
      const e = event || window.event

      // Calc cursor position from the:
      // - left edge of the viewport
      const cursorXfromViewport = e.touches ? e.touches[0].pageX : e.pageX

      // Calc Cursor Position from the:
      // - left edge of the window
      // to consider any page scrolling
      const cursorXfromWindow = cursorXfromViewport - window.pageXOffset

      // Calc Cursor Position from the:
      // - left edge of the image
      const imagePosition = rightImageRef.current.getBoundingClientRect()
      let pos = cursorXfromWindow - imagePosition.left

      // Set minimum and maximum values to prevent the slider from overflowing
      const minPos = 0 + sliderLineWidth / 2
      const maxPos = containerWidth - sliderLineWidth / 2

      if (pos < minPos) pos = minPos
      if (pos > maxPos) pos = maxPos

      setSliderPosition(pos / containerWidth)

      // If there's a callback function, invoke it everytime the slider changes
      if (onSliderPositionChange) {
        onSliderPositionChange(pos / containerWidth)
      }
    }

    const startSliding = (e: any) => {
      // Prevent default behavior other than mobile scrolling
      if (!('touches' in e)) {
        e.preventDefault()
      }

      // Slide the image even if you just click or tap (not drag)
      handleSliding(e)

      window.addEventListener('mousemove', handleSliding) // 07
      window.addEventListener('touchmove', handleSliding) // 08
    }

    const finishSliding = () => {
      window.removeEventListener('mousemove', handleSliding)
      window.removeEventListener('touchmove', handleSliding)
    }

    const containerElement = containerRef.current

    if (allImagesLoaded) {
      // it's necessary to reset event handlers each time the canvasWidth changes

      // for mobile
      containerElement.addEventListener('touchstart', startSliding) // 01
      window.addEventListener('touchend', finishSliding) // 02

      // for desktop
      if (hover) {
        containerElement.addEventListener('mousemove', handleSliding) // 03
        containerElement.addEventListener('mouseleave', finishSliding) // 04
      } else {
        containerElement.addEventListener('mousedown', startSliding) // 05
        window.addEventListener('mouseup', finishSliding) // 06
      }

      // calc and set the container's size
      const leftImageWidthHeightRatio = leftImageRef.current.naturalHeight / leftImageRef.current.naturalWidth
      const rightImageWidthHeightRatio = rightImageRef.current.naturalHeight / rightImageRef.current.naturalWidth

      const idealWidthHeightRatio =
        aspectRatio === 'taller'
          ? Math.max(leftImageWidthHeightRatio, rightImageWidthHeightRatio)
          : Math.min(leftImageWidthHeightRatio, rightImageWidthHeightRatio)

      const idealContainerHeight = containerWidth * idealWidthHeightRatio

      setContainerHeight(idealContainerHeight)
    }

    return () => {
      // cleanup all event resteners
      containerElement.removeEventListener('touchstart', startSliding) // 01
      window.removeEventListener('touchend', finishSliding) // 02
      containerElement.removeEventListener('mousemove', handleSliding) // 03
      containerElement.removeEventListener('mouseleave', finishSliding) // 04
      containerElement.removeEventListener('mousedown', startSliding) // 05
      window.removeEventListener('mouseup', finishSliding) // 06
      window.removeEventListener('mousemove', handleSliding) // 07
      window.removeEventListener('touchmove', handleSliding) // 08
    }
    // eslint-disable-next-line
  }, [
    allImagesLoaded,
    aspectRatio,
    containerHeight,
    containerWidth,
    hover,
    sliderLineWidth,
  ])

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      boxSizing: 'border-box',
      position: 'relative',
      width: '100%',
      minHeight: '100%',
      overflow: 'hidden',
      cursor: 'ew-resize'
    },
    rightImage: {
      clip: `rect(auto, auto, auto, ${containerWidth * sliderPosition}px)`,
      display: 'block',
      height: '100%',
      objectFit: 'cover',
      position: 'absolute',
      width: '100%'
    },
    leftImage: {
      clip: `rect(auto, ${containerWidth * sliderPosition}px, auto, auto)`,
      display: 'block',
      height: '100%',
      objectFit: 'cover',
      position: 'absolute',
      width: '100%'
    },
    slider: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'center',
      left: `${containerWidth * sliderPosition - handleSize / 2}px`,
      position: 'absolute',
      top: 0,
      width: `${handleSize}px`
    },
    line: {
      background: sliderLineColor,
      flex: '0 1 auto',
      height: '100%',
      width: `${sliderLineWidth}px`,
      zIndex: 1
    },
    handle: {
      alignItems: 'center',
      borderRadius: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      flex: '1 0 auto',
      height: `${handleSize}px`,
      justifyContent: 'center',
      width: `${handleSize}px`,
      background: 'white'
    },
    leftLabel: {
      fontWeight: 500,
      background: '#111c',
      color: 'white',
      borderRadius: '0 8px 8px 0',
      padding: '.5rem 1rem',
      textTransform: 'uppercase',
      position: 'absolute',
      left: 0,
      bottom: '3%'
    },
    rightLabel: {
      fontWeight: 500,
      background: '#111c',
      color: 'white',
      borderRadius: '8px 0 0 8px',
      padding: '.5rem 1rem',
      textTransform: 'uppercase',
      position: 'absolute',
      right: 0,
      bottom: '3%'
    },
    leftLabelContainer: {
      clip: `rect(auto, ${containerWidth * sliderPosition}px, auto, auto)`,
      height: '100%',
      position: 'absolute',
      width: '100%'
    },
    rightLabelContainer: {
      clip: `rect(auto, auto, auto, ${containerWidth * sliderPosition}px)`,
      height: '100%',
      position: 'absolute',
      width: '100%'
    }
  }

  return (
    <>
      {skeleton && !allImagesLoaded && <div style={{ ...styles.container }}>{skeleton}</div>}

      <div
        style={{
          ...styles.container,
          display: allImagesLoaded ? 'block' : 'none'
        }}
        ref={containerRef}
        data-testid='container'
      >
        <img
          onLoad={() => setRightImgLoaded(true)}
          alt={rightImageAlt}
          data-testid='right-image'
          ref={rightImageRef}
          src={rightImage}
          style={styles.rightImage}
        />
        <img
          onLoad={() => setLeftImgLoaded(true)}
          alt={leftImageAlt}
          data-testid='left-image'
          ref={leftImageRef}
          src={leftImage}
          style={styles.leftImage}
        />
        <div style={styles.slider}>
          <div style={styles.line} />
          <div style={styles.handle}>
            <ChevronLeft fontSize='small' sx={{ position: 'absolute', mr: 2, color: '#222' }} />
            <ChevronRight fontSize='small' sx={{ position: 'absolute', ml: 2, color: '#222' }} />
          </div>
          <div style={styles.line} />
        </div>
        {/* labels */}
        {leftImageLabel && (
          <Box style={styles.leftLabelContainer}>
            <Typography sx={{ fontSize: { xs: 10, sm: 12 } }} style={styles.leftLabel}>
              {leftImageLabel}
            </Typography>
          </Box>
        )}
        {rightImageLabel && (
          <Box style={styles.rightLabelContainer}>
            <Typography sx={{ fontSize: { xs: 10, sm: 12 } }} style={styles.rightLabel}>
              {rightImageLabel}
            </Typography>
          </Box>
        )}
      </div>
    </>
  )
}

export default CompareImage
